import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Security,
  Warning,
  CheckCircle,
  Error,
  Info,
  Delete,
  Restore,
  Storage,
  CloudOff,
  CloudDone
} from '@mui/icons-material';
import baseURL from '../../baseURL';

const EmergencySystem = () => {
  const [emergencyPassword, setEmergencyPassword] = useState('');
  const [clientId, setClientId] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');
  const [monitoringStatus, setMonitoringStatus] = useState(null);
  const [hiddenStorageStatus, setHiddenStorageStatus] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  
  // VPS Backup System States
  const [emergencyStatus, setEmergencyStatus] = useState({
    isActive: false,
    backupId: null,
    restoreCode: null,
    progress: 0,
    status: 'idle',
    error: null,
    startTime: null,
    endTime: null
  });
  const [vpsHealth, setVpsHealth] = useState(null);
  const [cleanupConfirmDialog, setCleanupConfirmDialog] = useState(false);
  const [actionType, setActionType] = useState('');

  useEffect(() => {
    // Load monitoring status on component mount
    loadMonitoringStatus();
    loadEmergencyStatus();
    checkVPSHealth();
    
    // Poll emergency status every 2 seconds if active
    let interval;
    if (emergencyStatus.isActive) {
      interval = setInterval(loadEmergencyStatus, 2000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
    checkHiddenStorageStatus();
  }, []);

  // VPS Integration Functions
  const loadEmergencyStatus = async () => {
    try {
      const response = await fetch(`${baseURL}/emergency/status`);
      const data = await response.json();
      if (data.success) {
        setEmergencyStatus(data.data);
      }
    } catch (error) {
      console.error('Error loading emergency status:', error);
    }
  };

  const checkVPSHealth = async () => {
    try {
      const response = await fetch(`${baseURL}/emergency/vps-health`);
      const data = await response.json();
      if (data.success) {
        setVpsHealth(data.data);
      }
    } catch (error) {
      console.error('Error checking VPS health:', error);
      setVpsHealth({ status: 'unhealthy', error: error.message });
    }
  };

  const triggerEmergencyBackup = async () => {
    try {
      setError(null);
      setSuccess(null);

      const response = await fetch(`${baseURL}/emergency/backup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          emergencyPassword: emergencyPassword
        })
      });
      const data = await response.json();

      if (data.success) {
        setSuccess('Emergency backup process started');
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
        loadEmergencyStatus();
      } else {
        setError(data.message || 'Failed to start emergency backup');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      }
    } catch (error) {
      setError(error.message || 'Failed to start emergency backup');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const triggerEmergencyCleanup = async () => {
    try {
      setError(null);
      setSuccess(null);

      const response = await fetch(`${baseURL}/emergency/cleanup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          emergencyPassword: emergencyPassword,
          confirmCleanup: true
        })
      });
      const data = await response.json();

      if (data.success) {
        setSuccess('Emergency cleanup process started');
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
        loadEmergencyStatus();
      } else {
        setError(data.message || 'Failed to start emergency cleanup');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      }
    } catch (error) {
      setError(error.message || 'Failed to start emergency cleanup');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setCleanupConfirmDialog(false);
    }
  };

  const getRestoreCode = async () => {
    try {
      const response = await fetch(`${baseURL}/emergency/restore-code`);
      const data = await response.json();
      if (data.success) {
        return data.data;
      }
    } catch (error) {
      console.error('Error getting restore code:', error);
    }
    return null;
  };

  const resetEmergencyStatus = async () => {
    try {
      const response = await fetch(`${baseURL}/emergency/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setSuccess('Emergency status reset successfully');
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
        loadEmergencyStatus();
      }
    } catch (error) {
      setError(error.message || 'Failed to reset emergency status');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const loadMonitoringStatus = async () => {
    try {
      const response = await fetch(`${baseURL}/emergency/monitoring`);
      const data = await response.json();
      setMonitoringStatus(data);
    } catch (error) {
      console.error('Error loading monitoring status:', error);
    }
  };

  const checkHiddenStorageStatus = async () => {
    try {
      // This would be a custom endpoint to check hidden storage
      // For now, we'll simulate the status
      setHiddenStorageStatus({
        exists: true,
        files: ['hosts_backup', 'services_backup'],
        lastUpdated: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error checking hidden storage:', error);
    }
  };

  const handleEmergencyWipe = async () => {
    if (!emergencyPassword) {
      setError('Emergency password is required');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    setActionType('wipe');
    setConfirmDialog(true);
  };

  const confirmEmergencyWipe = async () => {
    try {
      setError(null);
      setSuccess(null);

      const response = await fetch(`${baseURL}/emergency/wipe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          emergencyPassword,
          clientId
        })
      });
      const data = await response.json();

      if (data.success) {
        setSuccess('Emergency wipe completed successfully');
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
        setEmergencyPassword('');
        loadMonitoringStatus();
      } else {
        setError(data.message || 'Emergency wipe failed');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      }
    } catch (error) {
      setError(error.message || 'Emergency wipe failed');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setConfirmDialog(false);
    }
  };

  const handleRestoreFromBackup = async () => {
    if (!clientId) {
      setError('Client ID is required for restore');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    setActionType('restore');
    setConfirmDialog(true);
  };

  const confirmRestoreFromBackup = async () => {
    try {
      setError(null);
      setSuccess(null);

      const response = await fetch(`${baseURL}/emergency/restore`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          clientId
        })
      });
      const data = await response.json();

      if (data.success) {
        setSuccess('System restored from backup successfully');
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
        loadMonitoringStatus();
      } else {
        setError(data.message || 'Restore failed');
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      }
    } catch (error) {
      setError(error.message || 'Restore failed');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setConfirmDialog(false);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog(false);
    setActionType('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'error';
      case 'monitoring': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle />;
      case 'inactive': return <Error />;
      case 'monitoring': return <Warning />;
      default: return <Info />;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Security color="primary" />
        Emergency System
      </Typography>

      <Grid container spacing={3}>
        {/* VPS Backup System */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom color="primary">
              <CloudDone sx={{ mr: 1, verticalAlign: 'middle' }} />
              Backup Management System
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {/* VPS Health Status */}
            {vpsHealth && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  VPS Status:
                </Typography>
                <Chip
                  icon={vpsHealth.status === 'healthy' ? <CheckCircle /> : <Error />}
                  label={vpsHealth.status === 'healthy' ? 'VPS Online' : 'VPS Offline'}
                  color={vpsHealth.status === 'healthy' ? 'success' : 'error'}
                  size="small"
                />
              </Box>
            )}

            {/* Emergency Status */}
            {emergencyStatus.isActive && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Backup Status: {emergencyStatus.status}
                </Typography>
                <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 1, mb: 1 }}>
                  <Box
                    sx={{
                      width: `${emergencyStatus.progress}%`,
                      bgcolor: 'primary.main',
                      height: 8,
                      borderRadius: 1,
                      transition: 'width 0.3s ease'
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Progress: {emergencyStatus.progress}%
                </Typography>
              </Box>
            )}

            {/* Restore Code Display */}
            {emergencyStatus.restoreCode && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Restore Code:
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="primary.contrastText">
                  {emergencyStatus.restoreCode}
                </Typography>
                <Typography variant="caption" color="primary.contrastText">
                  Save this code securely for system restore
                </Typography>
              </Box>
            )}

            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Emergency Password"
                type="password"
                value={emergencyPassword}
                onChange={(e) => setEmergencyPassword(e.target.value)}
                sx={{ mb: 2 }}
                helperText="Enter emergency password to start backup process"
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={triggerEmergencyBackup}
                startIcon={<CloudDone />}
                disabled={!emergencyPassword || emergencyStatus.isActive}
              >
                Start Backup
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => setCleanupConfirmDialog(true)}
                startIcon={<Delete />}
                disabled={emergencyStatus.status !== 'backup_completed'}
              >
                Start Cleanup
              </Button>
              <Button
                variant="outlined"
                onClick={resetEmergencyStatus}
                disabled={!emergencyStatus.isActive}
              >
                Reset
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* System Status */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              <Info sx={{ mr: 1, verticalAlign: 'middle' }} />
              System Status
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {monitoringStatus && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Monitoring Status:
                </Typography>
                <Chip
                  icon={getStatusIcon(monitoringStatus.status)}
                  label={monitoringStatus.status}
                  color={getStatusColor(monitoringStatus.status)}
                  sx={{ mb: 1 }}
                />
                {monitoringStatus.lastCheck && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    Last Check: {new Date(monitoringStatus.lastCheck).toLocaleString()}
                  </Typography>
                )}
              </Box>
            )}

            {hiddenStorageStatus && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Hidden Storage:
                </Typography>
                <Chip
                  icon={hiddenStorageStatus.exists ? <Storage /> : <CloudOff />}
                  label={hiddenStorageStatus.exists ? 'Active' : 'Inactive'}
                  color={hiddenStorageStatus.exists ? 'success' : 'error'}
                  sx={{ mb: 1 }}
                />
                {hiddenStorageStatus.files && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    Files: {hiddenStorageStatus.files.join(', ')}
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Emergency Actions Log */}
        <Grid item xs={12}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Emergency Actions Log
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <List>
              <ListItem>
                <ListItemIcon>
                  <Info color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="System Ready"
                  secondary="Emergency system is operational and ready for use"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CloudDone color="success" />
                </ListItemIcon>
                <ListItemText
                  primary="VPS Connection"
                  secondary="Connected to VPS emergency app for backup operations"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Storage color="info" />
                </ListItemIcon>
                <ListItemText
                  primary="Hidden Storage"
                  secondary="Emergency password and transaction data stored in hidden system files"
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={handleCloseConfirmDialog}>
        <DialogTitle>
          {actionType === 'wipe' ? 'Confirm Emergency Wipe' : 'Confirm Restore'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {actionType === 'wipe' 
              ? 'Are you sure you want to trigger emergency wipe? This will delete all illegal data and code files.'
              : 'Are you sure you want to restore from backup? This will restore the illegal database and code files.'
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog}>Cancel</Button>
          <Button 
            onClick={actionType === 'wipe' ? confirmEmergencyWipe : confirmRestoreFromBackup}
            color={actionType === 'wipe' ? 'error' : 'primary'}
            variant="contained"
          >
            {actionType === 'wipe' ? 'Confirm Wipe' : 'Confirm Restore'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cleanup Confirmation Dialog */}
      <Dialog open={cleanupConfirmDialog} onClose={() => setCleanupConfirmDialog(false)}>
        <DialogTitle>
          Confirm Emergency Cleanup
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            <strong>WARNING:</strong> This will permanently delete all secondary database data and files.
          </Typography>
          <Typography gutterBottom>
            Make sure you have saved the restore code: <strong>{emergencyStatus.restoreCode}</strong>
          </Typography>
          <Typography>
            This action cannot be undone. The system will continue running with only the primary database.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCleanupConfirmDialog(false)}>Cancel</Button>
          <Button 
            onClick={triggerEmergencyCleanup}
            color="error"
            variant="contained"
          >
            Confirm Cleanup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EmergencySystem;













